import * as React from "react";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import MenuItem from "@mui/material/MenuItem";
import NotificationsIcon from "@mui/icons-material/Notifications";
import { styled, useTheme } from "@mui/material/styles";
import {
  ClickAwayListener,
  Divider,
  Grow,
  Menu,
  MenuList,
  Paper,
  Popper,
  Stack,
} from "@mui/material";
import useTokenCheck from "../utilities/backend/Auth";
import { Link, useNavigate } from "react-router-dom";
import { removeToken, setRole } from "../utilities/redux/actions";
import { useDispatch, useSelector } from "react-redux";
import {
  ApiException,
  EditUserDto,
  NotificationsClient,
  TokenDto,
  UserNotificationDto,
  UsersClient,
} from "../utilities/backend/client";
import { RootState } from "../utilities/redux/store";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { SVG } from "./svg";
import NotificationItem from "./NotificationItem";
import { useState } from "react";
import useHandleApiError from "../utilities/backend/Auth/useHandleApiError";
import {
  defaultImage,
  getUrlBlobContainerImage,
} from "../resources/imageConstants";
import TableRowsIcon from "@mui/icons-material/TableRows";
import AddIcon from "@mui/icons-material/Add";
import Logo from "../resources/logo.png";

interface MenuButtonProps {
  buttonText: string;
  menuItems: Array<{ label: string; onClick: () => void }>;
  open: boolean;
  onMouseEnter: (event: React.MouseEvent<HTMLElement>) => void;
  onClose: () => void;
  anchorEl: HTMLElement | null;
  ariaLabel: string;
}

const MenuButton: React.FC<MenuButtonProps> = ({
  buttonText,
  menuItems,
  open,
  onMouseEnter,
  onClose,
  anchorEl,
  ariaLabel,
}) => (
  <>
    <Button
      aria-haspopup="true"
      aria-expanded={open ? "true" : undefined}
      sx={{
        my: 2,
        color: open ? "#83ba3f" : "white",
        position: "relative",
        textTransform: "none",
        letterSpacing: 0,
        fontWeight: 500,
        fontFamily: "Roboto, Sans-serif",
        fontSize: 16,
        ml: "16px",
        display: "inline-flex",
        alignItems: "center",
        "&:hover": {
          color: "#83ba3f",
          transition: "transform 0.3s ease",
        },
        "&:hover::after": {
          content: '""',
          position: "absolute",
          bottom: "-5px",
          left: 0,
          width: "100%",
          height: "3px",
          backgroundColor: "#83ba3f",
          transform: "scaleX(1)",
          transition: "transform 0.3s ease",
        },
        "&::after": {
          content: '""',
          position: "absolute",
          bottom: "-5px",
          left: 0,
          width: "100%",
          height: "3px",
          backgroundColor: open ? "#83ba3f" : "transparent",
          transition: "background-color 0.3s ease",
        },
      }}
      onMouseEnter={onMouseEnter}
    >
      {buttonText}
      <KeyboardArrowDownIcon
        sx={{
          ml: "5px",
          mr: "3px",
          fontSize: 20,
          fontWeight: "bold",
          stroke: "currentColor",
          strokeWidth: 1,
        }}
      />
    </Button>
    <Menu
      sx={{
        top: 5,
        letterSpacing: 0,
        fontSize: 18,
        "& .MuiPaper-root": {
          borderRadius: "0px",
          backgroundColor: "black",
          color: "white",
        },
        "& .MuiList-root": {
          backgroundColor: "black",
          paddingTop: 0,
          paddingBottom: 0,
          boxShadow: "none",
          borderRadius: 0,
          "& .MuiMenuItem-root": {
            backgroundColor: "black",
            padding: "8px 16px",
            fontSize: 16,
            fontWeight: 500,
            fontFamily: "Poppins, Sans-serif",
            "&:hover": {
              backgroundColor: "#7a7a7a",
              color: "white",
            },
          },
        },
      }}
      id={ariaLabel}
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      MenuListProps={{
        "aria-labelledby": ariaLabel,
      }}
      slotProps={{
        paper: {
          onMouseLeave: onClose,
        },
      }}
    >
      {menuItems.map((item, index) => (
        <MenuItem
          key={index}
          sx={{
            fontSize: 16,
            fontWeight: 500,
            fontFamily: "Poppins, Sans-serif",
          }}
          onClick={item.onClick}
        >
          {item.label}
        </MenuItem>
      ))}
    </Menu>
  </>
);

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  gap: 12,
  marginBottom: 32,
}));

const StyledMenuItemMobile = styled(MenuItem)(({ theme }) => ({
  gap: 12,
  fontSize: 16,
  fontWeight: 500,
  p: 0,
  m: 0,
  minHeightww: 20,
}));

const StackContainer = styled(Stack)(({ theme }) => ({
  marginLeft: "62px",
  "@media (max-width: 1285px)": {
    marginLeft: 0,
  },
}));

const InvestAppBar = () => {
  const userClient = new UsersClient();
  const notificationClient = new NotificationsClient();
  const [user, setUser] = React.useState<EditUserDto>({} as EditUserDto);
  const [notifications, setNotifications] =
    React.useState<UserNotificationDto[]>();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [open, setOpen] = React.useState(false);
  const [openMobile, setOpenMobile] = React.useState(false);
  const [apiError, setApiError] = useState<ApiException | null>(null);
  const anchorRef = React.useRef<HTMLButtonElement>(null);
  const anchorRefMobile = React.useRef<HTMLButtonElement>(null);
  const [openNotification, setOpenNotification] = React.useState(false);
  const anchorNotRef = React.useRef<HTMLButtonElement>(null);
  const anchorNotMobileRef = React.useRef<HTMLButtonElement>(null);
  const [openNotificationMobile, setOpenNotificationMobile] =
    React.useState(false);
  const token: TokenDto = {
    token: useSelector((state: RootState) => state.token.token) || "",
  };
  const theme = useTheme();

  const isAdmin =
    token && token.token
      ? JSON.parse(atob(token.token.split(".")[1])).role === "Admin"
      : false;
  useHandleApiError(apiError);

  const [openInvestors, setOpenInvestors] = useState(false);
  const [openInvestorsMobile, setOpenInvestorsMobile] = useState(false);

  const [openGroupLeaders, setOpenGroupLeaders] = useState(false);
  const [openGroupLeadersMobile, setOpenGroupLeadersMobile] = useState(false);

  const [openRaiseMoney, setOpenRaiseMoney] = useState(false);
  const [openRaiseMoneyMobile, setOpenRaiseMoneyMobile] = useState(false);

  const [openAboutCataCap, setOpenAboutCataCap] = useState(false);
  const [openAboutCataCapMobile, setAboutCataCapMobile] = useState(false);

  const [openMyCataCapMenu, setOpenMyCataCapMenu] = useState(false);
  const [anchorElMenu, setAnchorElMenu] = React.useState<null | HTMLElement>(
    null
  );

  const handleMouseEnterInvestors = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElMenu(event.currentTarget);
    setOpenInvestors(true);
  };

  const handleMouseEnterGroupLeaders = (
    event: React.MouseEvent<HTMLElement>
  ) => {
    setAnchorElMenu(event.currentTarget);
    setOpenGroupLeaders(true);
  };

  const handleMouseEnterRaiseMoney = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElMenu(event.currentTarget);
    setOpenRaiseMoney(true);
  };

  const handleMouseEnterAboutCatacap = (
    event: React.MouseEvent<HTMLElement>
  ) => {
    setAnchorElMenu(event.currentTarget);
    setOpenAboutCataCap(true);
  };

  const handleMouseEnterInvestorsMobile = (
    event: React.MouseEvent<HTMLElement>
  ) => {
    setAnchorElMenu(event.currentTarget);
    setOpenInvestorsMobile((prevState) => !prevState);
  };

  const handleMouseEnterGroupLeadersMobile = (
    event: React.MouseEvent<HTMLElement>
  ) => {
    setAnchorElMenu(event.currentTarget);
    setOpenGroupLeadersMobile((prevState) => !prevState);
  };

  const handleMouseEnterRaiseMoneyMobile = (
    event: React.MouseEvent<HTMLElement>
  ) => {
    setOpenRaiseMoneyMobile((prevState) => !prevState);
  };

  const handleMouseEnterAboutCataCapMobile = (
    event: React.MouseEvent<HTMLElement>
  ) => {
    setAboutCataCapMobile((prevState) => !prevState);
  };

  const handleMouseEnterMyCataCap = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElMenu(event.currentTarget);
    setOpenMyCataCapMenu(true);
  };

  const handleCloseMenu = () => {
    setAnchorElMenu(null);
    setOpenRaiseMoney(false);
    setOpenInvestors(false);
    setOpenGroupLeaders(false);
    setOpenMyCataCapMenu(false);
    setOpenAboutCataCap(false);
  };

  React.useEffect(() => {
    if (user.token === undefined && token.token !== "") {
      Promise.all([
        userClient.users_GetUser(token),
        notificationClient.notifications_GetAllNotificationByUser(token),
      ])
        .then((response) => {
          setUser(response[0]);
          setNotifications(response[1]);
        })
        .catch((error: ApiException) => {
          setApiError(error);
        });
    }
  }, []);

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleToggleMobile = () => {
    setOpenMobile((prevOpen) => !prevOpen);
  };

  const handleNotificationOpen = () => {
    setOpenNotification((prevOpen) => !prevOpen);
  };

  const handleClose = (event: Event | React.SyntheticEvent) => {
    if (
      anchorRef.current &&
      anchorRef.current.contains(event.target as HTMLElement)
    ) {
      return;
    }

    setOpen(false);
    setOpenRaiseMoneyMobile(false);
    setOpenInvestorsMobile(false);
    setOpenGroupLeadersMobile(false);
    setAboutCataCapMobile(false);
  };

  const handleCloseMobile = (event: Event | React.SyntheticEvent) => {
    if (
      anchorRefMobile.current &&
      anchorRefMobile.current.contains(event.target as HTMLElement)
    ) {
      return;
    }

    setOpenMobile(false);
  };

  const handleCloseNotification = (event: Event | React.SyntheticEvent) => {
    if (
      anchorNotRef.current &&
      anchorNotRef.current.contains(event.target as HTMLElement)
    ) {
      return;
    }

    setOpenNotification(false);
  };

  const handleCloseNotificationMobile = (
    event: Event | React.SyntheticEvent
  ) => {
    if (
      anchorNotMobileRef.current &&
      anchorNotMobileRef.current.contains(event.target as HTMLElement)
    ) {
      return;
    }

    setOpenNotification(false);
  };

  const handleNotificationMobileOpen = () => {
    setOpenNotificationMobile((prevOpen) => !prevOpen);
  };

  function handleListKeyDown(event: React.KeyboardEvent) {
    if (event.key === "Tab") {
      event.preventDefault();
      setOpen(false);
    } else if (event.key === "Escape") {
      setOpen(false);
    }
  }

  function handleListKeyDownMobile(event: React.KeyboardEvent) {
    if (event.key === "Tab") {
      event.preventDefault();
      setOpenMobile(false);
    } else if (event.key === "Escape") {
      setOpenMobile(false);
    }
  }
  const isTokenPresent = useTokenCheck();

  function login() {
    navigate("/login");
  }

  function logOut() {
    setUser({});
    dispatch(removeToken());
    dispatch(setRole(""));
    localStorage.clear();
    navigate("/login");
  }

  function portfolio() {
    navigate("/portfolio");
  }

  function register() {
    navigate("/register");
  }

  function raiseMoneyClick() {
    window.location.href = "https://catacap.org/raise-money/";
  }

  function investmentChampionClick() {
    window.location.href = "https://catacap.org/investment-champion/";
  }

  function groupsClick() {
    window.location.href = "https://catacap.org/groups";
  }

  function forCompaniesClick() {
    window.location.href = "https://catacap.org/companies/";
  }

  function findInvestmentsClick() {
    navigate("/find");
  }

  function aboutCataCapClick() {
    window.location.href = "https://catacap.org/about/";
  }

  function caseStudiesClick() {
    window.location.href = "https://catacap.org/case-studies/";
  }

  function fagsClick() {
    window.location.href = "https://catacap.org/faqs/";
  }

  function community() {
    navigate("/community");
  }

  function onNotificationClick(notification: UserNotificationDto) {
    notification.isRead = true;
    notificationClient
      .notifications_UpdateNotification(notification.id!, notification)
      .then(() => {
        setOpenNotification(false);
        notificationClient
          .notifications_GetAllNotificationByUser(token)
          .then((response) => {
            setNotifications(response);
          })
          .catch((error) => {
            console.log(error);
          });
        if (notification.urlToRedirect!.startsWith("/")) {
          navigate(notification.urlToRedirect!);
        } else if (notification.urlToRedirect!.startsWith("http")) {
          window.open(notification.urlToRedirect!, "_blank");
        }
      })
      .catch((error) => {
        setApiError(error);
      });
  }

  function MarkAllNotificationAsRead() {
    notificationClient
      .notifications_UpdateAllNotificationForUser(token)
      .then(() => {
        notificationClient
          .notifications_GetAllNotificationByUser(token)
          .then((response) => {
            setNotifications(response);
          })
          .catch((error) => {
            setApiError(error);
          });
      })
      .catch((error) => {
        setApiError(error);
      });
  }

  const handleClickSupport = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    window.location.href = `mailto:support@impactree.org`;
  };

  const handleAddFunds = () => {
    setOpen(false);
    localStorage.setItem(
      "campaignMinInvestment",
      JSON.stringify(250)
    );
    localStorage.removeItem("recommendationData") // remove the recommendation data from local storage if it exists   
    navigate("/invest-payment", { state: { investmentAmount: 250 }, replace: true });
  }

  const linkTo = isTokenPresent
    ? "https://catacap.org"
    : "https://www.catacap.org";

  const menuConfig = [
    {
      buttonText: "For Donors/Investors",
      openState: openInvestors,
      onMouseEnter: handleMouseEnterInvestors,
      onClose: handleCloseMenu,
      menuItems: [
        {
          label: "Choose Investments",
          onClick: () => {
            setOpenInvestors(false);
            findInvestmentsClick();
          },
        },
        {
          label: "Champion Deals",
          onClick: () => {
            setOpenInvestors(false);
            investmentChampionClick();
          },
        },
      ],
      ariaLabel: "investors-menu",
    },
    {
      buttonText: "For Group Leaders",
      openState: openGroupLeaders,
      onMouseEnter: handleMouseEnterGroupLeaders,
      onClose: handleCloseMenu,
      menuItems: [
        {
          label: "Create a Group",
          onClick: () => {
            setOpenGroupLeaders(false);
            groupsClick();
          },
        },
        {
          label: "For Companies",
          onClick: () => {
            setOpenGroupLeaders(false);
            forCompaniesClick();
          },
        },
      ],
      ariaLabel: "group-leaders-menu",
    },
    {
      buttonText: "For Investments",
      openState: openRaiseMoney,
      onMouseEnter: handleMouseEnterRaiseMoney,
      onClose: handleCloseMenu,
      menuItems: [
        {
          label: "Raise Money",
          onClick: () => {
            setOpenRaiseMoney(false);
            raiseMoneyClick();
          },
        },
      ],
      ariaLabel: "raise-money-menu",
    },
    {
      buttonText: "About CataCap",
      openState: openAboutCataCap,
      onMouseEnter: handleMouseEnterAboutCatacap,
      onClose: handleCloseMenu,
      menuItems: [
        {
          label: "About CataCap",
          onClick: () => {
            setOpenAboutCataCap(false);
            aboutCataCapClick();
          },
        },
        {
          label: "Case Studies",
          onClick: () => {
            setOpenAboutCataCap(false);
            caseStudiesClick();
          },
        },
        {
          label: "FAQs",
          onClick: () => {
            setOpenAboutCataCap(false);
            fagsClick();
          },
        },
      ],
      ariaLabel: "about-catacap-menu",
    },
  ];

  return (
    <AppBar
      position="static"
      sx={{
        zIndex: 2,
        height: "102px",
        position: "relative",
        backgroundColor: "black",
        backgroundSize: "cover",
      }}
    >
      <Toolbar
        disableGutters
        sx={{ height: "100%", position: "relative", zIndex: 5 }}
      >
        <StackContainer>
          <Link to={linkTo} style={{ marginBottom: "6px" }}>
            <img src={Logo as unknown as string} height={97} width={192} />
          </Link>
        </StackContainer>
        {!isTokenPresent && (
          <>
            <Box
              sx={{
                flexDirection: "row",
                flexGrow: 1,
                display: { xs: "none", md: "flex" },
                alignItems: "center",
                ml: "40px",
                "@media (max-width: 1285px)": {
                  marginLeft: 0,
                  display: "none",
                },
              }}
            >
              {menuConfig.map((config, index) => (
                <MenuButton
                  key={index}
                  buttonText={config.buttonText}
                  menuItems={config.menuItems}
                  open={config.openState}
                  onMouseEnter={config.onMouseEnter}
                  onClose={config.onClose}
                  anchorEl={anchorElMenu}
                  ariaLabel={config.ariaLabel}
                />
              ))}
            </Box>
          </>
        )}
        {isTokenPresent ? (
          isAdmin ? (
            <Box
              width={"80%"}
              justifyContent={"flex-end"}
              sx={{
                flexDirection: "row",
                display: { xs: "none", md: "flex" },
                marginRight: "40px",
                "@media (max-width: 1285px)": {
                  marginRight: 0,
                  display: "none",
                },
              }}
            >
              <Button
                variant="contained"
                sx={{
                  my: 2,
                  padding: "6px 21px",
                  textTransform: "none",
                  letterSpacing: 0,
                  fontWeight: 500,
                  fontSize: 15,
                  borderColor: "#81ba3f",
                  backgroundColor: "#81ba3f",
                  color: "black",
                  "&.MuiButtonBase-root:hover": {
                    backgroundColor: "#205483",
                    borderColor: "#205483",
                    color: "white",
                  },
                }}
                onClick={() => logOut()}
              >
                Log Out
              </Button>
            </Box>
          ) : (
            <Box
              sx={{
                alignItems: "center",
                width: "100%",
                flexDirection: "row",
                display: { xs: "none", md: "flex" },
                justifyContent: "space-between",
                ml: "40px",
                "@media (max-width: 1285px)": {
                  ml: 0,
                  justifyContent: "end",
                },
              }}
            >
              <Box
                sx={{
                  alignItems: "center",
                  flexDirection: "row",
                  display: {
                    xs: "none",
                    sm: "none",
                    md: "none",
                    lg: "flex",
                    xl: "flex",
                  },

                  width: "100%",
                }}
              >
                {menuConfig.map((config, index) => (
                  <MenuButton
                    key={index}
                    buttonText={config.buttonText}
                    menuItems={config.menuItems}
                    open={config.openState}
                    onMouseEnter={config.onMouseEnter}
                    onClose={config.onClose}
                    anchorEl={anchorElMenu}
                    ariaLabel={config.ariaLabel}
                  />
                ))}
                <Button
                  id="my-cata-cap-button"
                  aria-controls={
                    openMyCataCapMenu ? "my-cata-cap-menu" : undefined
                  }
                  aria-haspopup="true"
                  aria-expanded={openMyCataCapMenu ? "true" : undefined}
                  sx={{
                    my: 2,
                    color: openMyCataCapMenu ? "#83ba3f" : "white",
                    position: "relative",
                    textTransform: "none",
                    letterSpacing: 0,
                    fontWeight: 500,
                    fontFamily: "Roboto, Sans-serif",
                    fontSize: 16,
                    ml: "20px",
                    display: "inline-flex",
                    alignItems: "center",
                    "&:hover": {
                      color: "#83ba3f",
                      transition: "transform 0.3s ease",
                    },
                    "&:hover::after": {
                      content: '""',
                      position: "absolute",
                      bottom: "-5px",
                      left: 0,
                      width: "100%",
                      height: "3px",
                      backgroundColor: "#83ba3f",
                      transform: "scaleX(1)",
                      transition: "transform 0.3s ease",
                    },
                    "&::after": {
                      content: '""',
                      position: "absolute",
                      bottom: "-5px",
                      left: 0,
                      width: "100%",
                      height: "3px",
                      backgroundColor: openMyCataCapMenu
                        ? "#83ba3f"
                        : "transparent",
                      transition: "background-color 0.3s ease",
                    },
                  }}
                  onMouseEnter={handleMouseEnterMyCataCap}
                >
                  My CataCap
                  <KeyboardArrowDownIcon />
                </Button>
                <Menu
                  sx={{
                    top: 5,
                    letterSpacing: 0,
                    fontSize: 18,
                    "& .MuiPaper-root": {
                      borderRadius: "0px",
                      backgroundColor: "black",
                      color: "white",
                    },
                    "& .MuiList-root": {
                      backgroundColor: "black",
                      paddingTop: 0,
                      paddingBottom: 0,
                      boxShadow: "none",
                      borderRadius: 0,
                      "& .MuiMenuItem-root": {
                        backgroundColor: "black",
                        padding: "8px 16px",
                        fontSize: 16,
                        fontWeight: 500,
                        fontFamily: "Poppins, Sans-serif",
                        "&:hover": {
                          backgroundColor: "#7a7a7a",
                          color: "white",
                        },
                      },
                    },
                  }}
                  id="my-cata-cap-menu"
                  anchorEl={anchorElMenu}
                  open={openMyCataCapMenu}
                  onClose={handleCloseMenu}
                  MenuListProps={{
                    "aria-labelledby": "my-cata-cap-button",
                  }}
                  slotProps={{
                    paper: {
                      onMouseLeave: handleCloseMenu,
                    },
                  }}
                >
                  <MenuItem
                    onClick={() => {
                      setOpenMyCataCapMenu(false);
                      portfolio();
                    }}
                  >
                    Portfolio
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      setOpenMyCataCapMenu(false);
                      community();
                    }}
                  >
                    Community
                  </MenuItem>
                </Menu>
              </Box>
              <Box
                sx={{
                  alignItems: "center",
                  flexDirection: "row",
                  display: { xs: "none", md: "flex" },
                }}
              >
                <IconButton ref={anchorNotRef} onClick={handleNotificationOpen}>
                  <NotificationsIcon sx={{ color: "white" }} />
                </IconButton>
                <Popper
                  open={openNotification}
                  anchorEl={anchorNotRef.current}
                  role={undefined}
                  placement="bottom-start"
                  transition
                  disablePortal
                  style={{ zIndex: 1 }}
                >
                  {({ TransitionProps, placement }) => (
                    <Grow
                      {...TransitionProps}
                      style={{
                        padding: "10px",
                        borderRadius: "16px",
                        boxShadow: "0px 5px 25px 0px rgba(0, 0, 0, 0.12)",
                        width: 245,
                        transformOrigin:
                          placement === "bottom-start"
                            ? "left top"
                            : "left bottom",
                      }}
                    >
                      <Paper>
                        <Box
                          sx={{
                            padding: "10px",
                            maxHeight: 400,
                            overflowY: "auto",
                            "&::-webkit-scrollbar": {
                              width: "6px",
                            },
                            "&::-webkit-scrollbar-thumb": {
                              backgroundColor: "#888",
                              borderRadius: "6px",
                            },
                            "&::-webkit-scrollbar-thumb:hover": {
                              backgroundColor: "#555",
                            },
                          }}
                        >
                          <ClickAwayListener
                            onClickAway={handleCloseNotification}
                          >
                            <Stack spacing={0}>
                              {notifications && notifications.length ? (
                                <>
                                  <Button
                                    sx={{ marginBottom: "10px" }}
                                    variant="contained"
                                    onClick={() => MarkAllNotificationAsRead()}
                                  >
                                    Mark all as read
                                  </Button>
                                  {notifications.map((notification) => (
                                    <div
                                      key={notification.id!}
                                      onClick={() =>
                                        onNotificationClick(notification)
                                      }
                                    >
                                      <NotificationItem
                                        notification={notification}
                                      />
                                    </div>
                                  ))}
                                </>
                              ) : (
                                <Typography
                                  align="center"
                                  sx={{
                                    width: "100%",
                                    fontFamily: "Ubuntu",
                                    fontSize: 16,
                                    fontWeight: 400,
                                    color: "#474747",
                                  }}
                                >
                                  No Notifications yet
                                </Typography>
                              )}
                            </Stack>
                          </ClickAwayListener>
                        </Box>
                      </Paper>
                    </Grow>
                  )}
                </Popper>
                <Divider
                  orientation="vertical"
                  sx={{ backgroundColor: "white" }}
                />
                <Box
                  height={30}
                  width={30}
                  sx={{ borderRadius: "8px", border: "solid 2px white" }}
                >
                  <img
                    style={{
                      width: "100%",
                      height: "100%",
                      borderRadius: "8px",
                    }}
                    src={
                      user.pictureFileName
                        ? getUrlBlobContainerImage(user?.pictureFileName)
                        : defaultImage
                    }
                    alt="img"
                  />
                </Box>
                <IconButton
                  sx={{ color: "white" }}
                  ref={anchorRef}
                  id="composition-button"
                  aria-controls={open ? "composition-menu" : undefined}
                  aria-expanded={open ? "true" : undefined}
                  aria-haspopup="true"
                  onClick={handleToggle}
                >
                  <KeyboardArrowDownIcon />
                </IconButton>
                <Popper
                  open={open}
                  anchorEl={anchorRef.current}
                  role={undefined}
                  placement="bottom-start"
                  transition
                  disablePortal
                  style={{ zIndex: 1 }}
                >
                  {({ TransitionProps, placement }) => (
                    <Grow
                      {...TransitionProps}
                      style={{
                        padding: "20px",
                        borderRadius: "16px",
                        boxShadow: "0px 5px 25px 0px rgba(0, 0, 0, 0.12)",
                        width: 245,
                        transformOrigin:
                          placement === "bottom-start"
                            ? "left top"
                            : "left bottom",
                      }}
                    >
                      <Paper>
                        <ClickAwayListener onClickAway={handleClose}>
                          <MenuList
                            autoFocusItem={open}
                            id="composition-menu"
                            aria-labelledby="composition-button"
                            onKeyDown={handleListKeyDown}
                          >
                            <StyledMenuItem
                              onClick={() => {
                                setOpen(false);
                                navigate("/");
                              }}
                            >
                              <SVG.Home /> Home
                            </StyledMenuItem>
                            <StyledMenuItem
                              onClick={handleAddFunds}
                            >
                              <SVG.Money /> Add Funds
                            </StyledMenuItem>
                            <Link
                              to={"https://www.catacap.org/raisemoney/"}
                              style={{
                                textDecoration: "none",
                                color: "#000000de",
                              }}
                            >
                              <StyledMenuItem
                                onClick={() => {
                                  setOpen(false);
                                }}
                              >
                                <SVG.Ticket />
                                Raise Money
                              </StyledMenuItem>
                            </Link>
                            <StyledMenuItem
                              onClick={() => {
                                setOpen(false);
                                navigate("/settings");
                              }}
                            >
                              <SVG.User /> My Account
                            </StyledMenuItem>
                            <StyledMenuItem
                              onClick={(event: any) => {
                                setOpen(false);
                                handleClickSupport(event);
                              }}
                            >
                              <SVG.Dialog />
                              Support
                            </StyledMenuItem>
                            <Box
                              sx={{
                                "@media (max-width: 1200px)": {
                                  display: "block",
                                },
                                "@media (min-width: 1200px)": {
                                  display: "none",
                                },
                              }}
                            >
                              <StyledMenuItem
                                onClick={() => {
                                  setOpenMobile(false);
                                  navigate("/find");
                                }}
                              >
                                <SVG.Find /> Find Investments
                              </StyledMenuItem>
                              <StyledMenuItem
                                onClick={(event: any) => {
                                  setOpen(false);
                                  investmentChampionClick();
                                }}
                              >
                                Champion Deals
                              </StyledMenuItem>
                              <StyledMenuItem
                                onClick={(event: any) => {
                                  setOpen(false);
                                  groupsClick();
                                }}
                              >
                                Create a Group
                              </StyledMenuItem>
                              <StyledMenuItem
                                onClick={(event: any) => {
                                  setOpen(false);
                                  forCompaniesClick();
                                }}
                              >
                                For Companies
                              </StyledMenuItem>
                            </Box>

                            <Divider
                              sx={{
                                width: "100%",
                                height: "1px",
                                background: "#E8E8E8",
                                marginBottom: "32px !important",
                              }}
                            />
                            <StyledMenuItem
                              sx={{ marginBottom: 0 }}
                              onClick={() => {
                                setOpen(false);
                                logOut();
                              }}
                            >
                              <SVG.Logout />
                              Log out
                            </StyledMenuItem>
                          </MenuList>
                        </ClickAwayListener>
                      </Paper>
                    </Grow>
                  )}
                </Popper>
              </Box>
            </Box>
          )
        ) : (
          <Box
            justifyContent={"flex-end"}
            sx={{
              flexDirection: "row",
              display: { xs: "none", md: "flex" },
              marginRight: "44px",
              "@media (max-width: 1285px)": {
                padding: "2px 0px",
                marginRight: "3px",
                display: "none",
              },
            }}
          >
            <Button
              variant="outlined"
              sx={{
                padding: "6px 23px",
                color: "black",
                display: "block",
                textTransform: "none",
                fontWeight: 500,
                fontSize: 15,
                lineHeight: "21.6px",
                borderColor: "#81ba3f",
                backgroundColor: "#81ba3f",
                marginRight: "35px",
                letterSpacing: 0,
                "&.MuiButtonBase-root:hover": {
                  backgroundColor: "#205483",
                  borderColor: "#205483",
                  color: "white",
                  fontWeight: "500",
                },
              }}
              onClick={login}
            >
              Log In
            </Button>
            <Button
              variant="contained"
              sx={{
                padding: "6px 23px",
                textTransform: "none",
                letterSpacing: 0,
                fontWeight: 500,
                fontSize: 15,
                borderColor: "#81ba3f",
                backgroundColor: "#81ba3f",
                color: "black",
                "&.MuiButtonBase-root:hover": {
                  backgroundColor: "#205483",
                  borderColor: "#205483",
                  color: "white",
                },
              }}
              onClick={register}
            >
              Sign Up
            </Button>
          </Box>
        )}
        {isTokenPresent ? (
          <Box
            sx={{ display: { xs: "flex", md: "none", width: "100%" }, mr: 1 }}
          >
            <Box
              sx={{
                display: {
                  xs: "flex",
                  md: "none",
                  width: "100%",
                  justifyContent: "end",
                },
              }}
            >
              <IconButton
                ref={anchorNotMobileRef}
                onClick={handleNotificationMobileOpen}
              >
                <NotificationsIcon sx={{ color: "white" }} />
              </IconButton>
              <Popper
                open={openNotificationMobile}
                anchorEl={anchorNotMobileRef.current}
                role={undefined}
                placement="bottom-start"
                transition
                disablePortal
                style={{ zIndex: 1 }}
              >
                {({ TransitionProps, placement }) => (
                  <Grow
                    {...TransitionProps}
                    style={{
                      padding: "10px",
                      borderRadius: "16px",
                      boxShadow: "0px 5px 25px 0px rgba(0, 0, 0, 0.12)",
                      width: 245,
                      transformOrigin:
                        placement === "bottom-start"
                          ? "left top"
                          : "left bottom",
                    }}
                  >
                    <Paper>
                      <Box
                        sx={{
                          padding: "10px",
                          maxHeight: 400,
                          overflowY: "auto",
                          "&::-webkit-scrollbar": {
                            width: "6px",
                          },
                          "&::-webkit-scrollbar-thumb": {
                            backgroundColor: "#888",
                            borderRadius: "6px",
                          },
                          "&::-webkit-scrollbar-thumb:hover": {
                            backgroundColor: "#555",
                          },
                        }}
                      >
                        <ClickAwayListener
                          onClickAway={handleCloseNotificationMobile}
                        >
                          <Stack spacing={0}>
                            {notifications && notifications.length ? (
                              <>
                                <Button
                                  sx={{ marginBottom: "10px" }}
                                  variant="contained"
                                  onClick={() => MarkAllNotificationAsRead()}
                                >
                                  Mark all as read
                                </Button>
                                {notifications.map((notification) => (
                                  <div
                                    key={notification.id!}
                                    onClick={() =>
                                      onNotificationClick(notification)
                                    }
                                  >
                                    <NotificationItem
                                      notification={notification}
                                    />
                                  </div>
                                ))}
                              </>
                            ) : (
                              <Typography
                                align="center"
                                sx={{
                                  width: "100%",
                                  fontFamily: "Ubuntu",
                                  fontSize: 16,
                                  fontWeight: 400,
                                  color: "#474747",
                                }}
                              >
                                No Notifications yet
                              </Typography>
                            )}
                          </Stack>
                        </ClickAwayListener>
                      </Box>
                    </Paper>
                  </Grow>
                )}
              </Popper>
              <Divider
                orientation="vertical"
                sx={{ mr: "15px", backgroundColor: "white" }}
              />
              <Box
                height={30}
                width={30}
                sx={{ borderRadius: "8px", border: "solid 2px white" }}
              >
                <img
                  style={{ width: "100%", height: "100%", borderRadius: "8px" }}
                  src={
                    user.pictureFileName
                      ? getUrlBlobContainerImage(user?.pictureFileName)
                      : defaultImage
                  }
                  alt="img"
                />
              </Box>
              <IconButton
                sx={{ color: "white" }}
                ref={anchorRefMobile}
                id="composition-button"
                aria-controls={openMobile ? "composition-menu" : undefined}
                aria-expanded={openMobile ? "true" : undefined}
                aria-haspopup="true"
                onClick={handleToggleMobile}
              >
                <KeyboardArrowDownIcon />
              </IconButton>
              <Popper
                open={openMobile}
                anchorEl={anchorRefMobile.current}
                role={undefined}
                placement="bottom-start"
                transition
                disablePortal
                style={{ zIndex: 1 }}
              >
                {({ TransitionProps, placement }) => (
                  <Grow
                    {...TransitionProps}
                    style={{
                      padding: "20px",
                      borderRadius: "16px",
                      boxShadow: "0px 5px 25px 0px rgba(0, 0, 0, 0.12)",
                      width: 245,
                      transformOrigin:
                        placement === "bottom-start"
                          ? "left top"
                          : "left bottom",
                    }}
                  >
                    <Paper>
                      <ClickAwayListener onClickAway={handleCloseMobile}>
                        <MenuList
                          autoFocusItem={openMobile}
                          id="composition-menu"
                          aria-labelledby="composition-button"
                          onKeyDown={handleListKeyDownMobile}
                        >
                          <StyledMenuItem
                            onClick={() => {
                              setOpenMobile(false);
                              navigate("/find");
                            }}
                          >
                            <SVG.Find /> Find Investments
                          </StyledMenuItem>
                          <StyledMenuItem
                            onClick={() => {
                              setOpenMobile(false);
                              navigate("/community");
                            }}
                          >
                            <SVG.Comm /> Community
                          </StyledMenuItem>
                          <StyledMenuItem
                            onClick={() => {
                              setOpenMobile(false);
                              navigate("/portfolio");
                            }}
                          >
                            <SVG.MyPortfolio /> My Portfolio
                          </StyledMenuItem>
                          <StyledMenuItem
                            onClick={() => {
                              setOpenMobile(false);
                              navigate("/");
                            }}
                          >
                            <SVG.Home /> Home
                          </StyledMenuItem>
                          <StyledMenuItem
                            onClick={handleAddFunds}
                          >
                            <SVG.Money /> Add Funds
                          </StyledMenuItem>
                          <Link
                            to={"https://www.catacap.org/raisemoney/"}
                            style={{
                              textDecoration: "none",
                              color: "#000000de",
                            }}
                          >
                            <StyledMenuItem
                              onClick={() => {
                                setOpenMobile(false);
                              }}
                            >
                              <SVG.Ticket />
                              Raise Money
                            </StyledMenuItem>
                          </Link>
                          <Link
                            to={"https://catacap.org/about/"}
                            style={{
                              textDecoration: "none",
                              color: "#000000de",
                            }}
                          >
                            <StyledMenuItem
                              onClick={() => {
                                setOpenMobile(false);
                              }}
                            >
                              <SVG.Ticket />
                              About CataCap
                            </StyledMenuItem>
                          </Link>
                          <Link
                            to={"https://catacap.org/case-studies/"}
                            style={{
                              textDecoration: "none",
                              color: "#000000de",
                            }}
                          >
                            <StyledMenuItem
                              onClick={() => {
                                setOpenMobile(false);
                              }}
                            >
                              <SVG.Ticket />
                              Case Studies
                            </StyledMenuItem>
                          </Link>
                          <Link
                            to={"https://catacap.org/faqs/"}
                            style={{
                              textDecoration: "none",
                              color: "#000000de",
                            }}
                          >
                            <StyledMenuItem
                              onClick={() => {
                                setOpenMobile(false);
                              }}
                            >
                              <SVG.Ticket />
                              FAQs
                            </StyledMenuItem>
                          </Link>
                          <StyledMenuItem
                            onClick={() => {
                              setOpenMobile(false);
                              navigate("/settings");
                            }}
                          >
                            <SVG.User /> My Account
                          </StyledMenuItem>
                          <StyledMenuItem
                            onClick={(event: any) => {
                              setOpen(false);
                              handleClickSupport(event);
                            }}
                          >
                            <SVG.Dialog />
                            Support
                          </StyledMenuItem>
                          <StyledMenuItem
                            onClick={(event: any) => {
                              setOpen(false);
                              investmentChampionClick();
                            }}
                          >
                            Champion Deals
                          </StyledMenuItem>

                          <StyledMenuItem
                            onClick={(event: any) => {
                              setOpen(false);
                              groupsClick();
                            }}
                          >
                            Create a Group
                          </StyledMenuItem>
                          <StyledMenuItem
                            onClick={(event: any) => {
                              setOpen(false);
                              forCompaniesClick();
                            }}
                          >
                            For Companies
                          </StyledMenuItem>
                          <Divider
                            sx={{
                              width: "100%",
                              height: "1px",
                              background: "#E8E8E8",
                              marginBottom: "32px !important",
                            }}
                          />
                          <StyledMenuItem
                            sx={{ marginBottom: 0 }}
                            onClick={() => {
                              setOpenMobile(false);
                              logOut();
                            }}
                          >
                            <SVG.Logout />
                            Log out
                          </StyledMenuItem>
                        </MenuList>
                      </ClickAwayListener>
                    </Paper>
                  </Grow>
                )}
              </Popper>
            </Box>
          </Box>
        ) : (
          <Box
            width={"100%"}
            justifyContent={"flex-end"}
            sx={{
              flexDirection: "row",
              marginRight: "40px",
              display: { xs: "flex", sm: "flex", md: "flex", lg: "none" },
              "@media (max-width: 1285px)": {
                display: "flex",
                marginRight: "10px",
              },
            }}
          >
            <IconButton
              sx={{ color: "white" }}
              ref={anchorRef}
              id="composition-button"
              aria-controls={open ? "composition-menu" : undefined}
              aria-expanded={open ? "true" : undefined}
              aria-haspopup="true"
              onClick={handleToggle}
            >
              {open ? (
                <AddIcon fontSize="large" sx={{ transform: "rotate(45deg)" }} />
              ) : (
                <TableRowsIcon />
              )}
            </IconButton>
            <Popper
              open={open}
              anchorEl={anchorRef.current}
              role={undefined}
              placement="bottom-start"
              transition
              disablePortal
              style={{
                zIndex: 1,
                width: "100%",
                border: 0,
              }}
            >
              {({ TransitionProps, placement }) => (
                <Grow
                  {...TransitionProps}
                  style={{
                    width: "100%",
                    transformOrigin:
                      placement === "bottom-start" ? "left top" : "left bottom",
                  }}
                >
                  <Paper
                    style={{
                      width: "100%",
                    }}
                  >
                    <ClickAwayListener onClickAway={handleClose}>
                      <MenuList
                        autoFocusItem={open}
                        id="composition-menu"
                        aria-labelledby="composition-button"
                        onKeyDown={handleListKeyDown}
                        style={{
                          background: "black",
                          color: "#ffffff",
                          width: "100%",
                          marginTop: "20px",
                          padding: 0,
                        }}
                      >
                        <StyledMenuItemMobile
                          id="raise-money-button"
                          aria-controls={
                            openInvestorsMobile
                              ? "raise-money-button"
                              : undefined
                          }
                          aria-haspopup="true"
                          aria-expanded={
                            openInvestorsMobile ? "true" : undefined
                          }
                          onClickCapture={handleMouseEnterInvestorsMobile}
                          style={{
                            width: "100%",
                            display: "flex",
                            justifyContent: "space-between",
                            backgroundColor: openInvestorsMobile
                              ? "#3e434a"
                              : "",
                            color: openInvestorsMobile ? "#ffffff" : "",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                            }}
                          >
                            <span>For Donors/Investors</span>
                            <KeyboardArrowDownIcon />
                          </Box>
                        </StyledMenuItemMobile>
                        {openInvestorsMobile && (
                          <>
                            <StyledMenuItemMobile
                              sx={{
                                fontSize: 16,
                                pl: 4,
                                fontWeight: 500,
                              }}
                              onClick={() => {
                                setOpen(false);
                                findInvestmentsClick();
                              }}
                            >
                              Choose Investments
                            </StyledMenuItemMobile>
                            <StyledMenuItemMobile
                              sx={{
                                fontSize: 16,
                                pl: 4,
                                fontWeight: 500,
                              }}
                              onClick={() => {
                                setOpen(false);
                                investmentChampionClick();
                              }}
                            >
                              Champion Deals
                            </StyledMenuItemMobile>
                          </>
                        )}

                        <StyledMenuItemMobile
                          id="raise-money-button"
                          aria-controls={
                            openGroupLeadersMobile
                              ? "raise-money-button"
                              : undefined
                          }
                          aria-haspopup="true"
                          aria-expanded={
                            openGroupLeadersMobile ? "true" : undefined
                          }
                          onClickCapture={handleMouseEnterGroupLeadersMobile}
                          style={{
                            width: "100%",
                            display: "flex",
                            justifyContent: "space-between",
                            backgroundColor: openGroupLeadersMobile
                              ? "#3e434a"
                              : "",
                            color: openGroupLeadersMobile ? "#ffffff" : "",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                            }}
                          >
                            <span>For Group Leaders</span>
                            <KeyboardArrowDownIcon />
                          </Box>
                        </StyledMenuItemMobile>
                        {openGroupLeadersMobile && (
                          <>
                            <StyledMenuItemMobile
                              sx={{
                                fontSize: 16,
                                pl: 4,
                                fontWeight: 500,
                              }}
                              onClick={() => {
                                setOpen(false);
                                groupsClick();
                              }}
                            >
                              Create a Group
                            </StyledMenuItemMobile>
                            <StyledMenuItemMobile
                              sx={{
                                fontSize: 16,
                                pl: 4,
                                fontWeight: 500,
                              }}
                              onClick={() => {
                                setOpen(false);
                                forCompaniesClick();
                              }}
                            >
                              For Companies
                            </StyledMenuItemMobile>
                          </>
                        )}

                        <StyledMenuItemMobile
                          id="raise-money-button"
                          aria-controls={
                            openRaiseMoneyMobile
                              ? "raise-money-button"
                              : undefined
                          }
                          aria-haspopup="true"
                          aria-expanded={
                            openRaiseMoneyMobile ? "true" : undefined
                          }
                          onClickCapture={handleMouseEnterRaiseMoneyMobile}
                          style={{
                            width: "100%",
                            display: "flex",
                            justifyContent: "space-between",
                            backgroundColor: openRaiseMoneyMobile
                              ? "#3e434a"
                              : "",
                            color: openRaiseMoneyMobile ? "#ffffff" : "",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                            }}
                          >
                            <span>For Investments</span>
                            <KeyboardArrowDownIcon />
                          </Box>
                        </StyledMenuItemMobile>
                        {openRaiseMoneyMobile && (
                          <StyledMenuItemMobile
                            sx={{
                              fontSize: 16,
                              pl: 4,
                              fontWeight: 500,
                            }}
                            onClick={() => {
                              setOpen(false);
                              raiseMoneyClick();
                            }}
                          >
                            Raise Money
                          </StyledMenuItemMobile>
                        )}

                        <StyledMenuItemMobile
                          id="about-catacap-button"
                          aria-controls={
                            openAboutCataCapMobile
                              ? "about-catacap-button"
                              : undefined
                          }
                          aria-haspopup="true"
                          aria-expanded={
                            openAboutCataCapMobile ? "true" : undefined
                          }
                          onClickCapture={handleMouseEnterAboutCataCapMobile}
                          style={{
                            width: "100%",
                            display: "flex",
                            justifyContent: "space-between",
                            backgroundColor: openAboutCataCapMobile
                              ? "#3e434a"
                              : "",
                            color: openAboutCataCapMobile ? "#ffffff" : "",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                            }}
                          >
                            <span>About CataCap</span>
                            <KeyboardArrowDownIcon />
                          </Box>
                        </StyledMenuItemMobile>
                        {openAboutCataCapMobile && (
                          <>
                            <StyledMenuItemMobile
                              sx={{
                                fontSize: 16,
                                pl: 4,
                                fontWeight: 500,
                              }}
                              onClick={() => {
                                setOpen(false);
                                aboutCataCapClick();
                              }}
                            >
                              About CataCap
                            </StyledMenuItemMobile>
                            <StyledMenuItemMobile
                              sx={{
                                fontSize: 16,
                                pl: 4,
                                fontWeight: 500,
                              }}
                              onClick={() => {
                                setOpen(false);
                                caseStudiesClick();
                              }}
                            >
                              Case Studies
                            </StyledMenuItemMobile>
                            <StyledMenuItemMobile
                              sx={{
                                fontSize: 16,
                                pl: 4,
                                fontWeight: 500,
                              }}
                              onClick={() => {
                                setOpen(false);
                                fagsClick();
                              }}
                            >
                              FAQs
                            </StyledMenuItemMobile>
                          </>
                        )}

                        <StyledMenuItemMobile
                          onClick={() => {
                            setOpen(false);
                            login();
                          }}
                          style={{
                            width: "100%",
                          }}
                        >
                          Log In
                        </StyledMenuItemMobile>

                        <StyledMenuItemMobile
                          onClick={() => {
                            setOpen(false);
                            register();
                          }}
                          style={{
                            width: "100%",
                          }}
                        >
                          Sign Up
                        </StyledMenuItemMobile>
                      </MenuList>
                    </ClickAwayListener>
                  </Paper>
                </Grow>
              )}
            </Popper>
          </Box>
        )}
      </Toolbar>
    </AppBar>
  );
};
export default InvestAppBar;
